import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { TruckGroup } from "../../types/truckGroup";
import { getTruckGroupsApi } from "../../api/truckGroupsApi";
import type { RootState } from "../index";

interface TruckGroupState {
  truckGroups: TruckGroup[];
  loading: boolean;
  error: string | null;
}

const initialState: TruckGroupState = {
  truckGroups: [],
  loading: false,
  error: null,
};

export const fetchTruckGroups = createAsyncThunk<TruckGroup[], void, { rejectValue: string }>(
  "truckGroups/fetchTruckGroups",
  async (_, { rejectWithValue }) => {
    try {
      return await getTruckGroupsApi();
      // return mapTruckGroupsResponse(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch truck groups");
    }
  },
);

const truckGroupSlice = createSlice({
  name: "truckGroups",
  initialState,
  reducers: {
    clearTruckGroupsError: (state) => {
      state.error = null;
    },
    updateTruckGroup: (state, action: PayloadAction<TruckGroup>) => {
      const index = state.truckGroups.findIndex((group) => group._id === action.payload._id);
      if (index !== -1) {
        state.truckGroups[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTruckGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTruckGroups.fulfilled, (state, action: PayloadAction<TruckGroup[]>) => {
        state.loading = false;
        state.truckGroups = action.payload;
      })
      .addCase(fetchTruckGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch truck groups";
      });
  },
});

export const { clearTruckGroupsError, updateTruckGroup } = truckGroupSlice.actions;

// Selectors
export const selectTruckGroups = (state: RootState) => state.truckGroups.truckGroups;
export const selectTruckGroupsLoading = (state: RootState) => state.truckGroups.loading;
export const selectTruckGroupsError = (state: RootState) => state.truckGroups.error;
export const selectTruckGroupById = createSelector(
  [selectTruckGroups, (_state: RootState, groupId: string) => groupId],
  (truckGroups, groupId) => truckGroups.find((group: TruckGroup) => group._id === groupId) || null,
);

export default truckGroupSlice.reducer;
